/** @jsxImportSource @emotion/react */

import { faUpRightFromSquare } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useParams } from "react-router-dom";
import { getFilledArrayOrDefault } from "../../../../utils";
import { FromLink } from "../../../../utils/useNavigateFrom";
import { flagOptions, attributeIsSet } from "./flagConstants";
import { MetaConditionsForm } from "./ColorCodeTriggerForm";
import FilterableConfigList from "./FilterableConfigList";

const defaultField = {
    field: {
        gte: false,
        lt: 16,
        flag: "danger",
        meta_conditions: []
    }
};

export default function FlagConfigForm({ name, title, fields, description, setArray, useDisplayFilter = false }) {
    const { tenant_id } = useParams();

    const getMetaTriggerTitle = (object) => {
        const link = <FromLink to={`/tenants/${tenant_id}/form-field-options/${object.options_name}`} target="_blank" rel="noopener noreferrer" >{object.meta_location} [{object.options_name}]<FontAwesomeIcon icon={faUpRightFromSquare} /> </FromLink>;
        // Generate string for included values
        const includeValuesString = object.include_values && object.include_values.length > 0
            ? `incl: ${object.include_values.join(", ")}`
            : "";
        // Generate string for excluded values
        const excludeValuesString = object.exclude_values && object.exclude_values.length > 0
            ? `excl: ${object.exclude_values.join(", ")}`
            : "";
        // Combine the options name with include and exclude values strings, separating with a space when necessary
        const titleParts = [includeValuesString, excludeValuesString].filter((part) => part !== "");
        return <span><span className="ms-3 me-2">{link}</span>{titleParts.join(" - ")}</span>;
    };

    const getFieldTitle = (object) => {
        const conditions: string[] = [];

        // Check and add the conditions based on the object's properties
        if (attributeIsSet(object.gte)) {
            conditions.push(`>= ${object.gte}`);
        }
        if (attributeIsSet(object.gt)) {
            conditions.push(`> ${object.gt}`);
        }
        if (attributeIsSet(object.lte)) {
            conditions.push(`<= ${object.lte}`);
        }
        if (attributeIsSet(object.lt)) {
            conditions.push(`< ${object.lt}`);
        }

        if (attributeIsSet(object.eq)) {
            conditions.push(`= ${object.eq}`);
        }

        const triggers = getFilledArrayOrDefault(object?.meta_conditions, []).map(getMetaTriggerTitle);


        // Join the conditions with a comma and space for readability
        const conditionsString = conditions.join(", ");

        // Return the formatted string
        return <span><b>{object.flag}</b> {conditionsString} {triggers}</span>;
    };
    const getFieldForm = (object, setObject) => [
        {
            label: "Greater than or equal to",
            name: "gte",
            type: "number",
        },
        {
            label: "Less than",
            name: "lt",
            type: "number",
        },
        {
            label: "Greater than",
            name: "gt",
            type: "number",
        },
        {
            label: "Less than or equal to",
            name: "lte",
            type: "number",
        },
        {
            label: "Equals",
            name: "eq",
            type: "text",
            description: "Use this field to compare string values"
        },
        {
            label: "Flag",
            type: "single-select",
            name: "flag",
            options: flagOptions
        },
        {
            type: "header",
            label: "Triggers conditions",
        },
        {
            type: "element",
            name: "meta_conditions",
            el: <MetaConditionsForm name="meta_conditions" title="Meta conditions" description="Define meta conditions to filter on" fields={object?.meta_conditions || []} setArray={(meta_conditions) => setObject({ ...object, meta_conditions })} />
        }
    ];
    return <FilterableConfigList
        useDisplayFilter={useDisplayFilter as false | string[]}
        name={name}
        title={title}
        description={description}
        fields={fields}
        setArray={setArray}
        defaultField={defaultField}
        getFieldForm={getFieldForm}
        getFieldTitle={getFieldTitle} />;

}
